import * as React from "react"
import { router, usePage } from "@inertiajs/react"
import { Sparkles } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { InlineField } from "@/components/ui/inline-edit"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { RichTextField } from "@/components/ui/rich-text-field"
import { PhotoSelectField, type PhotoSelection } from "@/components/photo-library/PhotoSelectField"
import type { LibraryPhoto } from "@/components/pages-cms/types"
import { useInlineRecord } from "@/hooks/use-inline-record"
import { postJson } from "@/lib/admin-fetch"
import { inlinePatch } from "@/lib/inline-save"
import type { PageProps } from "@/types/inertia"

export type ArticleData = {
  id?: number
  title_en: string
  title_es: string
  slug: string
  slug_es?: string | null
  body_en: string
  body_es: string
  excerpt_en: string
  excerpt_es: string
  category: string
  author: string
  read_time: number | null
  thumbnail: string
  published_at: string | null
  status: string
  brand?: string
  condition_slug?: string | null
  therapy_slug?: string | null
  page_slug?: string | null
  photo_id?: number | null
  photo_url?: string | null
  goal?: string | null
  goal_dirty?: boolean
  ai_queued?: boolean
  es_stale?: boolean
  ai_enabled?: boolean
}

export type ConditionOption = { slug: string; label: string; parent: string | null }
export type TherapyOption = { slug: string; label: string }
/** A static page that renders a related-articles list (Article::ARTICLE_HOST_PAGES). */
export type PageOption = { slug: string; label: string }

export function ArticleForm({
  article,
  actionUrl,
  method,
  conditions = [],
  therapies = [],
  pages = [],
  photoLibrary = [],
}: {
  article: ArticleData
  actionUrl: string
  method: "post" | "patch"
  conditions?: ConditionOption[]
  therapies?: TherapyOption[]
  pages?: PageOption[]
  photoLibrary?: LibraryPhoto[]
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const isNew = method === "post"
  const saved = !!article.id

  const fields = useInlineRecord({
    record: {
      goal: article.goal ?? "",
      title_en: article.title_en,
      title_es: article.title_es,
      slug: article.slug,
      slug_es: article.slug_es ?? "",
      body_en: article.body_en,
      body_es: article.body_es,
      excerpt_en: article.excerpt_en,
      excerpt_es: article.excerpt_es,
      category: article.category,
      author: article.author,
      read_time: article.read_time?.toString() ?? "",
      thumbnail: article.thumbnail,
      published_at: article.published_at ?? "",
      status: article.status,
      brand: article.brand ?? "gisela",
      condition_slug: article.condition_slug ?? "",
      page_slug: article.page_slug ?? "",
      therapy_slug: article.therapy_slug ?? "",
    },
    url: actionUrl,
    param: "article",
    enabled: !isNew,
  })
  const v = fields.values

  // Cover photo: a library pick submits photo_id, an upload submits the file.
  // On a saved article it goes up the moment it is chosen — a picture is
  // either the one you meant or it isn't, and you can see which from here.
  const [photo, setPhoto] = React.useState<PhotoSelection>({ kind: "unchanged" })
  const [processing, setProcessing] = React.useState(false)

  const [drafting, setDrafting] = React.useState(false)
  const [queueing, setQueueing] = React.useState(false)
  const [translating, setTranslating] = React.useState(false)
  const [goalActioned, setGoalActioned] = React.useState(false)
  const [aiError, setAiError] = React.useState<string | null>(null)
  const aiEnabled = article.ai_enabled ?? false

  function choosePhoto(selection: PhotoSelection) {
    setPhoto(selection)
    if (isNew || selection.kind === "unchanged") return
    if (selection.kind === "library") {
      inlinePatch(actionUrl, { article: { photo_id: selection.id } })
    } else {
      inlinePatch(actionUrl, { article: { photo: selection.file } }, { forceFormData: true })
    }
  }

  async function draftWithAi() {
    setDrafting(true)
    setAiError(null)
    try {
      const result = await postJson<{ content_en: Record<string, string> }>(
        `/admin/ai/articles/${article.id}/draft`,
        {
          goal: v.goal,
          content_en: { title: v.title_en, excerpt: v.excerpt_en, body: v.body_en },
        },
      )
      // A proposal, not a commit: each box that changed waits for its own tick.
      const drafted: Record<string, string> = {}
      if (result.content_en.title) drafted.title_en = result.content_en.title
      if (result.content_en.excerpt) drafted.excerpt_en = result.content_en.excerpt
      if (result.content_en.body) drafted.body_en = result.content_en.body
      fields.patch(drafted)
      setGoalActioned(true)
    } catch (e) {
      setAiError(e instanceof Error ? e.message : "Drafting failed.")
    } finally {
      setDrafting(false)
    }
  }

  function queueRefresh() {
    setQueueing(true)
    router.post(
      `/admin/ai/articles/${article.id}/queue`,
      {},
      { preserveScroll: true, onFinish: () => setQueueing(false) },
    )
  }

  function translate() {
    setTranslating(true)
    router.post(
      `/admin/ai/articles/${article.id}/translate`,
      {},
      { preserveScroll: true, onFinish: () => setTranslating(false) },
    )
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    router.post(
      actionUrl,
      {
        article: {
          ...v,
          read_time: v.read_time ? parseInt(v.read_time, 10) : null,
          published_at: v.published_at || null,
          ...(photo.kind === "upload" ? { photo: photo.file } : {}),
          ...(photo.kind === "library" ? { photo_id: photo.id } : {}),
        },
      },
      { forceFormData: photo.kind === "upload", onFinish: () => setProcessing(false) },
    )
  }

  const goalState = fields.state("goal")
  const titleEnState = fields.state("title_en")
  const titleEsState = fields.state("title_es")
  const slugState = fields.state("slug")
  const slugEsState = fields.state("slug_es")
  const brandState = fields.state("brand")
  const conditionState = fields.state("condition_slug")
  const pageState = fields.state("page_slug")
  const therapyState = fields.state("therapy_slug")
  const categoryState = fields.state("category")
  const authorState = fields.state("author")
  const readTimeState = fields.state("read_time", {
    serialize: (value) => (value ? parseInt(value, 10) : null),
  })
  const thumbnailState = fields.state("thumbnail")
  const publishedAtState = fields.state("published_at", { serialize: (value) => value || null })
  const statusState = fields.state("status")
  const excerptEnState = fields.state("excerpt_en")
  const excerptEsState = fields.state("excerpt_es")
  const bodyEnState = fields.state("body_en")
  const bodyEsState = fields.state("body_es")

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* The AI brief. Only offered once the article exists — drafting and
          queueing both address it by id. */}
      <section className="space-y-4">
        <h2>Brief</h2>
        <InlineField
          label="What should this article do? (used when drafting with AI)"
          htmlFor="goal"
          state={goalState}
        >
          <Input
            id="goal"
            value={v.goal}
            placeholder="e.g. Explain what ERP actually feels like, session by session, for someone put off by the word exposure"
            onChange={(e) => fields.set("goal", e.target.value)}
            onKeyDown={goalState.keys}
          />
        </InlineField>

        {saved && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={drafting || !aiEnabled}
              title={aiEnabled ? undefined : "Set ANTHROPIC_API_KEY to enable AI drafting"}
              onClick={draftWithAi}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {drafting ? "Drafting…" : "Draft with AI"}
            </Button>
            {article.es_stale ? (
              <Badge tone="signal">Spanish out of date</Badge>
            ) : (
              <Badge tone="muted">Spanish up to date</Badge>
            )}
            {aiEnabled && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={translating}
                onClick={translate}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                {translating ? "Translating…" : "Translate from English"}
              </Button>
            )}
          </div>
        )}

        {saved && article.goal_dirty && !goalActioned && (
          article.ai_queued ? (
            <div className="flex flex-wrap items-center gap-3">
              <Badge tone="accent">Queued for tonight&apos;s AI update</Badge>
              <p className="text-sm text-ink-muted">
                The nightly sync will rewrite this article from its brief and re-mirror the Spanish.
              </p>
            </div>
          ) : (
            <div className="space-y-3 rounded-lg border border-hairline bg-surface p-4">
              <p className="text-sm text-ink-body">
                The brief has changed since this article was written. Update it?
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={drafting || !aiEnabled}
                  title={aiEnabled ? undefined : "Set ANTHROPIC_API_KEY to enable instant updates"}
                  onClick={draftWithAi}
                >
                  <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                  {drafting ? "Drafting…" : "Update now (uses API credits)"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={queueing}
                  onClick={queueRefresh}
                >
                  {queueing ? "Queueing…" : "Update tonight (included in subscription)"}
                </Button>
              </div>
            </div>
          )
        )}

        {aiError && <p className="text-sm text-danger-display">{aiError}</p>}
        {!saved && (
          <p className="text-sm text-ink-muted">
            Create the article first — AI drafting and per-field saving both need an article to work on.
          </p>
        )}
      </section>

      {/* Titles */}
      <section className="space-y-4">
        <h2>Title</h2>
        <InlineField label="Title (English)" htmlFor="title_en" error={errors.title_en} state={titleEnState}>
          <Input
            id="title_en"
            value={v.title_en}
            onChange={(e) => fields.set("title_en", e.target.value)}
            onKeyDown={titleEnState.keys}
            required
            aria-invalid={!!errors.title_en}
          />
        </InlineField>
        <InlineField label="Title (Spanish)" htmlFor="title_es" state={titleEsState}>
          <Input
            id="title_es"
            value={v.title_es}
            onChange={(e) => fields.set("title_es", e.target.value)}
            onKeyDown={titleEsState.keys}
          />
        </InlineField>
        <InlineField label="URL slug" htmlFor="slug" error={errors.slug} state={slugState}>
          <Input
            id="slug"
            value={v.slug}
            onChange={(e) => fields.set("slug", e.target.value)}
            onKeyDown={slugState.keys}
            placeholder="auto-generated from title"
            aria-invalid={!!errors.slug}
          />
        </InlineField>
        <InlineField
          label="Spanish URL slug"
          htmlFor="slug_es"
          hint="Comes from the Spanish title when the translation lands. With one, the Spanish version lives at /blog plus this; without, at /es/blog plus the English slug."
          error={errors.slug_es}
          state={slugEsState}
        >
          <Input
            id="slug_es"
            value={v.slug_es}
            onChange={(e) => fields.set("slug_es", e.target.value)}
            onKeyDown={slugEsState.keys}
            placeholder="auto-generated from the Spanish title"
            aria-invalid={!!errors.slug_es}
          />
        </InlineField>
      </section>

      {/* Meta */}
      <section className="space-y-4">
        <h2>Meta</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField
            label="Website"
            htmlFor="brand"
            hint="Which blog this appears on. Only Gisela's articles go to the newsletter."
            state={brandState}
          >
            <Select id="brand" value={v.brand} onChange={(e) => fields.set("brand", e.target.value)}>
              <option value="gisela">Gisela Ramirez</option>
              <option value="sanctuary">Sanctuary Therapy Centre</option>
            </Select>
          </InlineField>
          <InlineField
            label="Supports which page?"
            htmlFor="condition_slug"
            hint="That page lists this article under “Related articles”."
            error={errors.condition_slug}
            state={conditionState}
          >
            <Select
              id="condition_slug"
              value={v.condition_slug}
              onChange={(e) => fields.set("condition_slug", e.target.value)}
              aria-invalid={!!errors.condition_slug}
            >
              <option value="">Not linked to a page</option>
              {conditions.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.parent ? `OCD — ${c.label}` : c.label}
                </option>
              ))}
            </Select>
          </InlineField>
          <InlineField
            label="Static page"
            htmlFor="page_slug"
            hint="For pages that aren't a condition or a therapy, such as Online Therapy."
            error={errors.page_slug}
            state={pageState}
          >
            <Select
              id="page_slug"
              value={v.page_slug}
              onChange={(e) => fields.set("page_slug", e.target.value)}
              aria-invalid={!!errors.page_slug}
            >
              <option value="">Not linked to a static page</option>
              {pages.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.label}
                </option>
              ))}
            </Select>
          </InlineField>
          <InlineField
            label="Therapy"
            htmlFor="therapy_slug"
            hint="That therapy's page lists this article under “Related articles” too."
            error={errors.therapy_slug}
            state={therapyState}
          >
            <Select
              id="therapy_slug"
              value={v.therapy_slug}
              onChange={(e) => fields.set("therapy_slug", e.target.value)}
              aria-invalid={!!errors.therapy_slug}
            >
              <option value="">Not linked to a therapy</option>
              {therapies.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label}
                </option>
              ))}
            </Select>
          </InlineField>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <InlineField label="Category tag" htmlFor="category" state={categoryState}>
            <Input
              id="category"
              value={v.category}
              onChange={(e) => fields.set("category", e.target.value)}
              onKeyDown={categoryState.keys}
              placeholder="Defaults to the linked page's name"
            />
          </InlineField>
          <InlineField label="Author" htmlFor="author" state={authorState}>
            <Input
              id="author"
              value={v.author}
              onChange={(e) => fields.set("author", e.target.value)}
              onKeyDown={authorState.keys}
            />
          </InlineField>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <InlineField label="Read time (mins)" htmlFor="read_time" state={readTimeState}>
            <Input
              id="read_time"
              type="number"
              min="1"
              value={v.read_time}
              onChange={(e) => fields.set("read_time", e.target.value)}
              onKeyDown={readTimeState.keys}
              placeholder="5"
            />
          </InlineField>
          <InlineField label="Emoji (fallback thumbnail)" htmlFor="thumbnail" state={thumbnailState}>
            <Input
              id="thumbnail"
              value={v.thumbnail}
              onChange={(e) => fields.set("thumbnail", e.target.value)}
              onKeyDown={thumbnailState.keys}
              placeholder="🧠"
            />
          </InlineField>
          <InlineField label="Published date" htmlFor="published_at" state={publishedAtState}>
            <Input
              id="published_at"
              type="date"
              value={v.published_at}
              onChange={(e) => fields.set("published_at", e.target.value)}
              onKeyDown={publishedAtState.keys}
            />
          </InlineField>
        </div>
        <InlineField label="Status" htmlFor="status" state={statusState}>
          <Select
            id="status"
            value={v.status}
            onChange={(e) => fields.set("status", e.target.value)}
            className="w-40"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </Select>
        </InlineField>
      </section>

      {/* Cover photo */}
      <section className="space-y-4">
        <h2>Cover photo</h2>
        <div className="space-y-1.5">
          <label htmlFor="photo">Cover photo</label>
          <PhotoSelectField
            library={photoLibrary}
            currentUrl={article.photo_url}
            value={photo}
            onChange={choosePhoto}
            shape="landscape"
            inputId="photo"
          />
          <p className="mt-1 text-xs text-ink-muted">
            The thumbnail on the blog and in &ldquo;Related articles&rdquo;. Uploads go into the photo library&apos;s
            Articles section; the emoji above stands in when there is no photo.
            {!isNew && " Choosing one saves it straight away."}
          </p>
        </div>
      </section>

      {/* Excerpt */}
      <section className="space-y-4">
        <h2>Excerpt</h2>
        <InlineField label="Excerpt (English)" htmlFor="excerpt_en" state={excerptEnState}>
          <Input
            id="excerpt_en"
            value={v.excerpt_en}
            onChange={(e) => fields.set("excerpt_en", e.target.value)}
            onKeyDown={excerptEnState.keys}
            placeholder="Short summary (1–2 sentences)"
          />
        </InlineField>
        <InlineField label="Excerpt (Spanish)" htmlFor="excerpt_es" state={excerptEsState}>
          <Input
            id="excerpt_es"
            value={v.excerpt_es}
            onChange={(e) => fields.set("excerpt_es", e.target.value)}
            onKeyDown={excerptEsState.keys}
            placeholder="Resumen corto"
          />
        </InlineField>
      </section>

      {/* Body */}
      <section className="space-y-4">
        <h2>Body</h2>
        {/* Keyed on the field's version so a discarded edit or an AI draft
            reaches the editor — RichTextField is uncontrolled. */}
        <InlineField label="Body (English)" error={errors.body_en} state={bodyEnState}>
          <RichTextField
            key={`body-en-${bodyEnState.version}`}
            defaultValue={bodyEnState.value}
            onChange={(md) => fields.set("body_en", md)}
            placeholder="Write the article body in English…"
          />
        </InlineField>
        <InlineField label="Body (Spanish)" state={bodyEsState}>
          <RichTextField
            key={`body-es-${bodyEsState.version}`}
            defaultValue={bodyEsState.value}
            onChange={(md) => fields.set("body_es", md)}
            placeholder="Escriba el cuerpo del artículo en español…"
          />
        </InlineField>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-6">
        {isNew ? (
          <>
            <Button type="submit" disabled={processing}>
              Save article
            </Button>
            <Button type="button" variant="ghost" onClick={() => window.history.back()}>
              Cancel
            </Button>
          </>
        ) : (
          <p className="text-sm text-ink-muted">
            Each change is kept when you tick it — there's nothing else to save.
          </p>
        )}
      </div>
    </form>
  )
}
